import type React from 'react'
import { useId, useState } from 'react'
import styles from './PillSelect.module.css'
import { Typography } from './Typography'

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
export type PillSelectOption = { value: string; label: string }

export type PillSelectProps = {
  /** 選択肢 */
  options: PillSelectOption[]
  /** 制御コンポーネント用: 現在の選択値 */
  value?: string
  /** 非制御コンポーネント用: 初期値 */
  defaultValue?: string
  /** 選択変更時コールバック */
  onChange?: (value: string) => void
  /**
   * 選択なし時（value が options に存在しない場合）のプレースホルダーラベル。
   * 省略時は options[0].label を表示する。
   */
  placeholder?: string
  disabled?: boolean
  className?: string
}

// ─────────────────────────────────────────────
// コンポーネント
// ─────────────────────────────────────────────
/**
 * 見た目がPill形状のSelectBoxコンポーネント。
 * 「すべてのコース▼」のようなフィルター選択UIに使用する。
 *
 * ネイティブ `<select>` を透明な状態でPillの上に重ねることで、
 * OSネイティブのピッカーUIを呼び出しながら、カスタムPillスタイルを維持する。
 *
 * @example
 * <PillSelect
 *   options={[{ value: "all", label: "すべてのコース" }, { value: "1", label: "観光コース" }]}
 *   value={selected}
 *   onChange={(v) => setSelected(v)}
 *   placeholder="すべてのコース"
 * />
 */
export const PillSelect = ({
  options,
  value,
  defaultValue,
  onChange,
  placeholder,
  disabled = false,
  className,
}: PillSelectProps) => {
  const selectId = useId()

  // value が渡された場合は制御コンポーネント、そうでない場合は内部 state で管理
  const isControlled = value !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue ?? '')
  const currentValue = isControlled ? value : internalValue

  const selectedLabel =
    options.find((opt) => opt.value === currentValue)?.label ?? placeholder ?? options[0]?.label ?? ''

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!isControlled) setInternalValue(e.target.value)
    onChange?.(e.target.value)
  }

  const rootClass = [styles.root, disabled ? styles.disabled : '', className ?? ''].filter(Boolean).join(' ')

  return (
    <div className={rootClass}>
      <Typography variant="overline" component="span" className={styles.label}>
        {selectedLabel}
      </Typography>
      <span className={styles.arrow} aria-hidden="true">
        ▼
      </span>
      <select
        id={selectId}
        className={styles.select}
        value={currentValue}
        disabled={disabled}
        aria-label={selectedLabel}
        onChange={handleChange}
      >
        {placeholder !== undefined && !options.find((opt) => opt.value === '') && (
          <option value="">{placeholder}</option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}
